import { Box, Tab, Tabs } from '@mui/material';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import {
  JobAssignStatus,
  JobQueueTableType,
} from 'pages/iPriseV2/common/enum/TeamUserRoles';
import JobQueueTableCommon from 'pages/iPriseV2/common/JobQueueTable';
import React, { useCallback } from 'react';

import ColumnsSidebar from './ColumnsSidebar';

const JobQueueTable: React.FC = () => {
  const { supportJobQueueService } = useStore();
  const {
    supportSubTab,
    teamLeadSubTab,
    pendingPage,
    pendingRowsPerPage,
    submittedJobPage,
    submittedJobRowsPerPage,
    newPage,
    newRowsPerPage,
    assignedPage,
    assignedRowsPerPage,
    readyForReviewPage,
    readyForReviewRowsPerPage,
    approvedPage,
    approvedRowsPerPage,
    selectedSort,
    searchTerm,
    percentage,
    mainTab,
  } = supportJobQueueService;

  const handleSupportSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      supportJobQueueService.setSupportSubTab(newValue);
    },
    [supportJobQueueService],
  );

  const handleTeamLeadSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      supportJobQueueService.setTeamLeadSubTab(newValue);
    },
    [supportJobQueueService],
  );

  const renderSupportQueue = () => (
    <>
      <Box className="sub-tabs-container">
        <Box className="sub-tabs-wrapper">
          <Tabs
            value={supportSubTab}
            onChange={handleSupportSubTabChange}
            className="sub-tabs"
            variant="scrollable"
            allowScrollButtonsMobile
            TabIndicatorProps={{
              className: 'sub-tab-indicator',
            }}>
            <Tab label="Pending" className="sub-tab" />
            <Tab label="Submitted Job Queue" className="sub-tab" />
          </Tabs>
        </Box>
      </Box>

      {supportSubTab === 0 && (
        <JobQueueTableCommon
          tableType={JobQueueTableType.SUPPORT_PENDING}
          status={JobAssignStatus.TL_APPROVED} // Example status
          queryKey="support-pending"
          page={pendingPage}
          rowsPerPage={pendingRowsPerPage}
          onPageChange={(p) => supportJobQueueService.setPendingPage(p)}
          onRowsPerPageChange={(r) =>
            supportJobQueueService.setPendingRowsPerPage(r)
          }
          popperId="support-pending-popper"
          selectedSort={selectedSort}
          searchTerm={searchTerm}
          percentage={percentage}
        />
      )}
      {supportSubTab === 1 && (
        <JobQueueTableCommon
          tableType={JobQueueTableType.SUPPORT_SUBMITTED}
          status={JobAssignStatus.SUBMITTED}
          queryKey="support-submitted"
          page={submittedJobPage}
          rowsPerPage={submittedJobRowsPerPage}
          onPageChange={(p) => supportJobQueueService.setSubmittedJobPage(p)}
          onRowsPerPageChange={(r) =>
            supportJobQueueService.setSubmittedJobRowsPerPage(r)
          }
          selectedSort={selectedSort}
          searchTerm={searchTerm}
          percentage={percentage}
          popperId="support-submitted-popper"
        />
      )}
    </>
  );

  const renderTeamLeadQueue = () => (
    <>
      <Box className="sub-tabs-container">
        <Box className="sub-tabs-wrapper">
          <Tabs
            value={teamLeadSubTab}
            onChange={handleTeamLeadSubTabChange}
            className="sub-tabs"
            variant="scrollable"
            allowScrollButtonsMobile
            TabIndicatorProps={{
              className: 'sub-tab-indicator',
            }}>
            <Tab label="New" className="sub-tab" />
            <Tab label="Assigned" className="sub-tab" />
            <Tab label="Ready for Review" className="sub-tab" />
            <Tab label="Approved" className="sub-tab" />
          </Tabs>
        </Box>
      </Box>

      {teamLeadSubTab === 0 && (
        <JobQueueTableCommon
          tableType={JobQueueTableType.SUPPORT_TL_NEW}
          status={JobAssignStatus.TL_UNALLOCATED}
          queryKey="tl-new"
          page={newPage}
          rowsPerPage={newRowsPerPage}
          onPageChange={(p) => supportJobQueueService.setNewPage(p)}
          onRowsPerPageChange={(r) =>
            supportJobQueueService.setNewRowsPerPage(r)
          }
          popperId="tl-new-popper"
          selectedSort={selectedSort}
          searchTerm={searchTerm}
          percentage={percentage}
        />
      )}
      {teamLeadSubTab === 1 && (
        <JobQueueTableCommon
          tableType={JobQueueTableType.SUPPORT_TL_ASSIGNED}
          status={JobAssignStatus.RECRUITER_ASSIGNED}
          queryKey="tl-assigned"
          page={assignedPage}
          rowsPerPage={assignedRowsPerPage}
          onPageChange={(p) => supportJobQueueService.setAssignedPage(p)}
          onRowsPerPageChange={(r) =>
            supportJobQueueService.setAssignedRowsPerPage(r)
          }
          popperId="tl-assigned-popper"
          selectedSort={selectedSort}
          searchTerm={searchTerm}
          percentage={percentage}
        />
      )}
      {teamLeadSubTab === 2 && (
        <JobQueueTableCommon
          tableType={JobQueueTableType.SUPPORT_TL_READY_FOR_REVIEW}
          status={JobAssignStatus.READY_FOR_REVIEW}
          queryKey="tl-ready-for-review"
          page={readyForReviewPage}
          rowsPerPage={readyForReviewRowsPerPage}
          onPageChange={(p) => supportJobQueueService.setReadyForReviewPage(p)}
          onRowsPerPageChange={(r) =>
            supportJobQueueService.setReadyForReviewRowsPerPage(r)
          }
          popperId="tl-ready-for-review-popper"
          selectedSort={selectedSort}
          searchTerm={searchTerm}
          percentage={percentage}
        />
      )}
      {teamLeadSubTab === 3 && (
        <JobQueueTableCommon
          tableType={JobQueueTableType.SUPPORT_TL_APPROVED}
          status={JobAssignStatus.TL_APPROVED}
          queryKey="tl-approved"
          page={approvedPage}
          rowsPerPage={approvedRowsPerPage}
          onPageChange={(p) => supportJobQueueService.setApprovedPage(p)}
          onRowsPerPageChange={(r) =>
            supportJobQueueService.setApprovedRowsPerPage(r)
          }
          popperId="tl-approved-popper"
          selectedSort={selectedSort}
          searchTerm={searchTerm}
          percentage={percentage}
        />
      )}
    </>
  );

  return (
    <Box className="table-container-with-sidebar">
      <Box className="table-main-content">
        {mainTab === 0 ? renderSupportQueue() : renderTeamLeadQueue()}
      </Box>
      <ColumnsSidebar />
    </Box>
  );
};

export default observer(JobQueueTable);
